/*
 * Project: Goalscape
 * File:    workspaceUtils.js
 * Path:    src/utils/workspaceUtils.js
 * Description: Utility functions for managing workspaces in Firebase.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement real-time updates for workspace changes using WebSockets for a more interactive experience.
 */
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

/*
 * Function: createWorkspace
 * Description: Creates a new workspace in Firebase with initial settings.
 * Parameters:
 *   data: object - The workspace data (name, description, etc.).
 *   user: object - The user creating the workspace.
 * Returns: Promise<DocumentReference> - Reference to the created workspace document.
 * Usage:
 *   const workspaceRef = await createWorkspace({ name: 'My Workspace' }, currentUser);
 * AI Hint: Implement server-side validation for workspace data to prevent invalid entries.
 */
export const createWorkspace = async (data, user) => {
    const workspaceData = {
        ...data,
        createdBy: user.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        members: {
            [user.uid]: {
                role: 'admin',
                addedAt: new Date().toISOString(),
                addedBy: user.uid
            }
        },
        projects: []
    };

    return await addDoc(collection(db, 'workspaces'), workspaceData);
};